/**
 * SelectDetailsPage
 *
 * Full-page mobile view for selecting which details to enable for a category.
 * Replaces the Select Details dialog on mobile.
 */

import React, { ReactElement, useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { SelectDetailsContent } from "./components/SelectDetailsContent";
import { useCategoryGroups } from "./hooks/useCategoryGroups";
import { useCategoryTransactions } from "./hooks/useCategoryTransactions";
import { useCategories } from "./hooks/useCategories";
import { useCategoryHelpers } from "./hooks/useCategoryHelpers";
import { api } from "../../services/api";
import { TransactionCategoryGroup } from "../../types";

interface SelectDetailsPageProps {
  workspaceId: string;
}

function SelectDetailsPage({ workspaceId }: SelectDetailsPageProps): ReactElement {
  const navigate = useNavigate();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const location = useLocation();
  const pathParts = location.pathname.split("/").filter(Boolean);
  const settingsIndex = pathParts.indexOf("settings");
  const typeParam = pathParts[settingsIndex + 2] as "expense" | "income" | undefined;
  const categoryId = pathParts[settingsIndex + 3];

  const type = typeParam === "income" ? "income" : "expense";

  const { categories, setCategories } = useCategories({ workspaceId, type });
  const { groups } = useCategoryGroups({ workspaceId, type });
  const { transactions } = useCategoryTransactions({ workspaceId, type });
  const { isDetailUsed, hasDetailSelectionChanged } = useCategoryHelpers({
    categories,
    groups,
    transactions,
  });

  const category = categories.find((c) => c.id === categoryId);
  const group: TransactionCategoryGroup | null =
    category?.groupId ? (groups.find((g) => g.id === category.groupId) ?? null) : null;

  const [enabledDetails, setEnabledDetails] = useState<string[]>([]);
  const [defaultValues, setDefaultValues] = useState<Record<string, string | number>>({});
  const [originalEnabledDetails, setOriginalEnabledDetails] = useState<string[]>([]);
  const [originalDefaultValues, setOriginalDefaultValues] = useState<Record<string, string | number>>({});
  const [savingDetails, setSavingDetails] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (category) {
      const enabled = category.enabledDetails ?? [];
      const defaults = category.defaultValues ?? {};
      setEnabledDetails([...enabled]);
      setDefaultValues({ ...defaults });
      setOriginalEnabledDetails([...enabled]);
      setOriginalDefaultValues({ ...defaults });
    }
  }, [category?.id]);

  const handleSave = async (): Promise<void> => {
    if (!category || !workspaceSlug) return;
    setSavingDetails(true);
    setError("");

    try {
      const cleanedDefaultValues: Record<string, string | number> = {};
      Object.entries(defaultValues).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          cleanedDefaultValues[key] = value;
        }
      });

      await api.updateCategory(
        workspaceId,
        category.id,
        undefined,
        undefined,
        undefined,
        undefined,
        enabledDetails,
        cleanedDefaultValues
      );

      const categoriesData = await api.getCategories(workspaceId, type);
      setCategories(categoriesData);
      navigate(`/${workspaceSlug}/settings/categories`, { replace: true });
    } catch (err: any) {
      setError(err.message || "Failed to save detail selection");
    } finally {
      setSavingDetails(false);
    }
  };

  const canSave =
    !savingDetails &&
    hasDetailSelectionChanged(enabledDetails, originalEnabledDetails, defaultValues, originalDefaultValues);

  if (!category) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0,
          position: "sticky",
          top: 0,
          zIndex: 10,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <IconButton
          component={Link}
          to={`/${workspaceSlug}/settings/categories`}
          size="medium"
          sx={{ minWidth: 48, minHeight: 48 }}
          aria-label="Back to Categories"
        >
          <ArrowBackIcon sx={{ fontSize: 24 }} />
        </IconButton>
        <Typography variant="h6" sx={{ flex: 1, pr: 1, fontSize: "1.125rem" }}>
          Select Details - {category.name}
        </Typography>
      </Paper>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          p: 2,
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {category && (
          <SelectDetailsContent
            category={category}
            group={group}
            enabledDetails={enabledDetails}
            setEnabledDetails={setEnabledDetails}
            defaultValues={defaultValues}
            setDefaultValues={setDefaultValues}
            isDetailUsed={isDetailUsed}
          />
        )}
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            onClick={() => navigate(`/${workspaceSlug}/settings/categories`, { replace: true })}
            disabled={savingDetails}
            sx={{ flex: 1, py: 1.5 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSave}
            sx={{ flex: 1, py: 1.5 }}
          >
            {savingDetails ? "Saving..." : "Save"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}

export default SelectDetailsPage;
